import type { ContenidoSemana } from '@app/shared';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { RedSocialIcon, type RedConLogo } from '../../../components/icons/RedSocialIcon';
import { Alert } from '../../../components/ui/alert';
import { useI18n } from '../../../i18n/I18nProvider';
import { getErrorMessage } from '../../../lib/api';
import { cn } from '../../../lib/cn';
import { qk } from '../../../lib/queryKeys';
import { metricasApi } from '../../admin/api/metricas.api';

// Indicadores de contenido orgánico (ciclo-metricas-embudo-y-contenido, R6, 28/09/2026):
// promedio semanal de alcance/interacciones/tiempo de reproducción entre los posts de
// Buffer. Tres mini-gráficos separados (decisión 6 del proposal) — las tres escalas son
// demasiado distintas para un solo eje. Clic en una barra abre el detalle de esa semana.

type Rango = 'semana' | 'mes' | 'tresMeses' | 'personalizado';

function isoHaceDias(dias: number): string {
  const d = new Date();
  d.setDate(d.getDate() - dias);
  return d.toISOString().slice(0, 10);
}

const DIAS_POR_RANGO: Record<Exclude<Rango, 'personalizado'>, number> = {
  semana: 7,
  mes: 30,
  tresMeses: 90,
};

// Los 4 canales: 3 vía Buffer (mismo texto exacto que BUFFER_CHANNEL_NAME en
// lib/buffer.ts del backend) + YouTube (28/09, su propio sync — nunca pasa por
// Buffer, se sube directo). El logo es el real de cada marca (Simple Icons),
// no un emoji — pedido explícito de Javier.
const CANALES: { canal: string; clave: RedConLogo }[] = [
  { canal: '📸 Instagram', clave: 'INSTAGRAM' },
  { canal: '🎵 TikTok', clave: 'TIKTOK' },
  { canal: '👥 Facebook', clave: 'FACEBOOK' },
  { canal: '▶️ YouTube', clave: 'YOUTUBE' },
];

function promedio(valores: number[]): number | null {
  return valores.length > 0
    ? Math.round((valores.reduce((a, b) => a + b, 0) / valores.length) * 10) / 10
    : null;
}

/** Recalcula cantidad/promedios de una semana con solo los posts de los canales
 * activos — mismo cálculo que hace el backend, pero acá filtrado (evita ir de
 * nuevo al servidor: la API ya trae el detalle por post de cada semana). */
function filtrarSemana(s: ContenidoSemana, canalesActivos: Set<string>): ContenidoSemana {
  const posts = s.posts.filter((p) => canalesActivos.has(p.canal));
  return {
    ...s,
    cantidadPosts: posts.length,
    // reach es null en Facebook y YouTube (D-029/D-031): se excluye del
    // promedio en vez de contar un 0 falso.
    reachProm: promedio(posts.filter((p) => p.reach !== null).map((p) => p.reach!)),
    // views (D-031): reproducciones, DISTINTO de reach (cuentas únicas). null
    // solo en Facebook.
    viewsProm: promedio(posts.filter((p) => p.views !== null).map((p) => p.views!)),
    interaccionesProm: promedio(posts.map((p) => p.interacciones)),
    tiempoReproduccionProm: promedio(
      posts.filter((p) => p.tiempoReproduccionProm !== null).map((p) => p.tiempoReproduccionProm!),
    ),
    posts,
  };
}

export function IndicadoresContenido(): JSX.Element {
  const es = useI18n();
  const t = es.publicar.indicadores;
  const [rango, setRango] = useState<Rango>('mes');
  const [desdePersonalizado, setDesdePersonalizado] = useState('');
  const [hastaPersonalizado, setHastaPersonalizado] = useState('');
  const [semanaSeleccionada, setSemanaSeleccionada] = useState<string | null>(null);
  const [canalesActivos, setCanalesActivos] = useState<Set<string>>(
    () => new Set(CANALES.map((c) => c.canal)),
  );

  const desde = rango === 'personalizado' ? desdePersonalizado : isoHaceDias(DIAS_POR_RANGO[rango]);
  const hasta = rango === 'personalizado' ? hastaPersonalizado : '';
  const params = { ...(desde ? { desde } : {}), ...(hasta ? { hasta } : {}) };

  const { data, isLoading, error } = useQuery({
    queryKey: qk.metricas.contenido(params),
    queryFn: () => metricasApi.contenido(params),
  });

  const semanas = useMemo(
    () => (data?.semanas ?? []).map((s) => filtrarSemana(s, canalesActivos)),
    [data, canalesActivos],
  );
  const detalle = useMemo(
    () => semanas.find((s) => s.semanaInicio === semanaSeleccionada) ?? null,
    [semanas, semanaSeleccionada],
  );

  const chartData = semanas.map((s) => ({
    semana: s.semanaInicio.slice(5), // "MM-DD", más compacto en el eje
    semanaCompleta: s.semanaInicio,
    reach: s.reachProm,
    // Cuántos posts de la semana tienen dato de reach — Facebook y YouTube
    // siempre null (D-029/D-031), los demás sí lo reportan.
    reachN: s.posts.filter((p) => p.reach !== null).length,
    views: s.viewsProm,
    // Solo Facebook no reporta views (D-031).
    viewsN: s.posts.filter((p) => p.views !== null).length,
    interacciones: s.interaccionesProm,
    tiempo: s.tiempoReproduccionProm,
    // Cuántos posts de la semana tienen ESTE dato — Facebook y YouTube nunca lo
    // reportan (0/101 y 0/109, medido en vivo el 28/09), Instagram a veces. Sin
    // esto, un promedio de 1 sola publicación se ve igual de "sólido" que uno de 5.
    tiempoN: s.posts.filter((p) => p.tiempoReproduccionProm !== null).length,
    cantidadPosts: s.cantidadPosts,
  }));

  const toggleCanal = (canal: string): void => {
    setCanalesActivos((prev) => {
      const next = new Set(prev);
      if (next.has(canal)) next.delete(canal);
      else next.add(canal);
      return next;
    });
  };

  return (
    <div className="flex h-full min-h-0 flex-col gap-4 overflow-y-auto rounded-lg border border-border-subtle bg-surface p-4 shadow-card">
      <div className="flex flex-wrap items-center gap-2">
        {(['semana', 'mes', 'tresMeses', 'personalizado'] as const).map((r) => (
          <button
            key={r}
            type="button"
            onClick={() => setRango(r)}
            className={cn(
              'rounded-md border px-3 py-1.5 text-xs font-medium transition-colors',
              rango === r
                ? 'border-marca-primario bg-surface-primario text-marca'
                : 'border-border-subtle text-text-subtle hover:border-border-strong',
            )}
          >
            {t.rango[r]}
          </button>
        ))}
        {rango === 'personalizado' && (
          <>
            <input
              type="date"
              value={desdePersonalizado}
              onChange={(e) => setDesdePersonalizado(e.target.value)}
              className="h-8 rounded-md border border-marca-neutro bg-surface px-2 text-xs"
            />
            <input
              type="date"
              value={hastaPersonalizado}
              onChange={(e) => setHastaPersonalizado(e.target.value)}
              className="h-8 rounded-md border border-marca-neutro bg-surface px-2 text-xs"
            />
          </>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {CANALES.map(({ canal, clave }) => {
          const activo = canalesActivos.has(canal);
          return (
            <button
              key={canal}
              type="button"
              aria-pressed={activo}
              title={es.publicar.redes[clave]}
              onClick={() => toggleCanal(canal)}
              className={cn(
                'flex h-9 w-9 items-center justify-center rounded-full border-2 transition-all',
                activo
                  ? 'border-marca-primario bg-surface-primario shadow-sm'
                  : 'border-transparent opacity-35 grayscale hover:opacity-70',
              )}
            >
              <RedSocialIcon red={clave} size={18} />
            </button>
          );
        })}
      </div>

      {error && <Alert variant="error">{getErrorMessage(error, es.errores.generico.algoSalioMal)}</Alert>}
      {isLoading && <p className="text-sm text-text-subtle">{t.cargando}</p>}
      {canalesActivos.size === 0 && <p className="text-sm text-warning">{t.sinRedes}</p>}
      {data && canalesActivos.size > 0 && semanas.every((s) => s.cantidadPosts === 0) && (
        <p className="text-sm text-text-subtle">{t.sinDatos}</p>
      )}

      {data && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-4">
          <MiniGrafico
            titulo={t.alcance}
            dataKey="reach"
            color="hsl(var(--marca-azul))"
            chartData={chartData}
            nKey="reachN"
            onSelect={setSemanaSeleccionada}
          />
          <MiniGrafico
            titulo={t.visualizaciones}
            dataKey="views"
            color="hsl(var(--marca-azul-claro))"
            chartData={chartData}
            nKey="viewsN"
            onSelect={setSemanaSeleccionada}
          />
          <MiniGrafico
            titulo={t.interacciones}
            dataKey="interacciones"
            color="hsl(var(--marca-amarillo))"
            chartData={chartData}
            onSelect={setSemanaSeleccionada}
          />
          <MiniGrafico
            titulo={t.tiempoReproduccion}
            dataKey="tiempo"
            color="hsl(var(--marca-naranja))"
            chartData={chartData}
            sufijo={t.segundos}
            nKey="tiempoN"
            onSelect={setSemanaSeleccionada}
          />
        </div>
      )}

      {detalle && <DetalleSemana semana={detalle} onCerrar={() => setSemanaSeleccionada(null)} />}
    </div>
  );
}

interface PuntoChart {
  semana: string;
  semanaCompleta: string;
  reach: number | null;
  reachN: number;
  views: number | null;
  viewsN: number;
  interacciones: number | null;
  tiempo: number | null;
  tiempoN: number;
  cantidadPosts: number;
}

function MiniGrafico({
  titulo,
  dataKey,
  color,
  chartData,
  sufijo,
  nKey,
  onSelect,
}: {
  titulo: string;
  dataKey: 'reach' | 'views' | 'interacciones' | 'tiempo';
  color: string;
  chartData: PuntoChart[];
  sufijo?: string;
  /** Cuando el dato no lo reportan todas las redes (tiempo de reproducción,
   * reach/views de Facebook, reach de YouTube): qué campo de PuntoChart tiene
   * el tamaño de muestra real, para mostrar "n=X" en el tooltip — un promedio
   * de 1 post no debería verse tan sólido como uno de 5. */
  nKey?: 'tiempoN' | 'reachN' | 'viewsN';
  onSelect: (semanaInicio: string) => void;
}): JSX.Element {
  return (
    <div className="rounded-md border border-marca-neutro bg-surface-sunken p-3 transition-shadow hover:shadow-md">
      <p className="mb-1 text-xs font-medium uppercase tracking-wide text-text-subtle">{titulo}</p>
      <div className="h-[180px]">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border-subtle))" vertical={false} />
            <XAxis dataKey="semana" tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
            <Tooltip
              formatter={(value, _name, item) => {
                if (value === null || value === undefined) return '—';
                const n = nKey ? (item.payload as PuntoChart | undefined)?.[nKey] : undefined;
                return `${String(value)}${sufijo ?? ''}${n !== undefined ? ` (n=${String(n)})` : ''}`;
              }}
            />
            <Bar
              dataKey={dataKey}
              fill={color}
              radius={[3, 3, 0, 0]}
              cursor="pointer"
              onClick={(data) => {
                const payload = (data as { payload?: PuntoChart }).payload;
                if (payload) onSelect(payload.semanaCompleta);
              }}
            />
            <Line
              type="monotone"
              dataKey={dataKey}
              stroke="hsl(var(--marca-primario-oscuro))"
              strokeWidth={2}
              dot={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

const CLAVE_POR_CANAL: Record<string, RedConLogo> = Object.fromEntries(
  CANALES.map((c) => [c.canal, c.clave]),
);

function DetalleSemana({ semana, onCerrar }: { semana: ContenidoSemana; onCerrar: () => void }): JSX.Element {
  const es = useI18n();
  const t = es.publicar.indicadores;
  return (
    <div className="rounded-md border border-marca-primario bg-surface-primario p-3">
      <div className="mb-2 flex items-center justify-between">
        <p className="text-sm font-semibold text-marca">
          {semana.semanaInicio} · {t.publicaciones.replace('{n}', String(semana.cantidadPosts))}
        </p>
        <button type="button" onClick={onCerrar} className="text-xs text-text-subtle hover:text-text-strong">
          ×
        </button>
      </div>
      {semana.cantidadPosts === 0 ? (
        <p className="text-xs text-text-subtle">{t.sinPublicaciones}</p>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {semana.posts.map((p) => (
            <li key={p.id} className="flex items-center gap-3 rounded bg-surface px-2 py-1.5 text-xs">
              <span className="shrink-0" title={p.canal}>
                {CLAVE_POR_CANAL[p.canal] ? (
                  <RedSocialIcon red={CLAVE_POR_CANAL[p.canal]!} size={13} />
                ) : (
                  <span className="text-text-faint">{p.canal}</span>
                )}
              </span>
              <span className="min-w-0 flex-1 truncate">{p.texto ?? '—'}</span>
              <span className="shrink-0 tabular-nums text-text-subtle">
                {t.alcance} {p.reach ?? '—'} · {t.visualizaciones} {p.views ?? '—'} · {t.interacciones}{' '}
                {p.interacciones}
                {p.tiempoReproduccionProm !== null && ` · ${String(p.tiempoReproduccionProm)}${t.segundos}`}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
